import { inject, Injectable, OnDestroy } from '@angular/core';
import {
  ErrorHandlerOptions,
  EsriErrorHandler,
  EsriErrorType,
  EsriRequestError
} from '@jeppesen-foreflight/jmkw-types';
import { NGXLogger } from 'ngx-logger';
import { Observable, of, Subject, Subscription, throwError } from 'rxjs';
import { catchError, debounceTime, finalize } from 'rxjs/operators';
import { CONFIG_TOKEN } from '../../jmkw.config';

/**
 * Configuration time for error handler debounce (in milliseconds)
 */
const ERROR_DEBOUNCE_TIME = 300;

/**
 * Service for classifying JMKW tile ERROR RESPONSES and dispatching to registered handlers.
 *
 * Plugins call {@link registerErrorHandler} to subscribe to errors matching a URL pattern.
 * `PurlyService` owns the actual `esriConfig.request.interceptors` registration and invokes
 * {@link handleJmkwTileError} from its interceptor's `error` callback — this avoids the
 * dual-interceptor problem (see JMKW-1863) where ArcGIS' `getInterceptor` picks a single
 * matching entry per URL and silently skips the others.
 */
@Injectable({ providedIn: 'root' })
export class EsriInterceptor implements OnDestroy {
  private readonly _logger = inject(NGXLogger);
  private readonly _config = inject(CONFIG_TOKEN);

  private readonly _errorHandlers: Map<
    string,
    Array<{ handler: EsriErrorHandler; options: ErrorHandlerOptions }>
  > = new Map();

  private readonly _subscriptions: Subscription = new Subscription();

  /**
   * Implements OnDestroy for proper cleanup
   */
  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  /**
   * Classify an error into an EsriErrorType
   * @param error The error to classify
   * @returns The classified error type
   */
  private classifyError(error: EsriRequestError): EsriErrorType {
    if (error.name === 'AbortError') return EsriErrorType.ABORT;

    if (error.details?.httpStatus) {
      const status = error.details.httpStatus;
      if (status === 0 || status >= 500) return EsriErrorType.NETWORK;
      if (status === 401 || status === 403) return EsriErrorType.AUTHENTICATION;
      if (status === 408) return EsriErrorType.TIMEOUT;
    }

    if (error.message?.toLowerCase().includes('timeout'))
      return EsriErrorType.TIMEOUT;
    if (error.message?.toLowerCase().includes('network'))
      return EsriErrorType.NETWORK;

    return EsriErrorType.UNKNOWN;
  }

  /**
   * Handle errors from JMKW tile requests.
   *
   * Called from `PurlyService`'s merged request interceptor `error` callback. Classifies
   * the error, extracts the URL from the error payload, and dispatches to any handlers
   * registered via {@link registerErrorHandler} that match the URL.
   *
   * When `hideEsriErrors` is set in the JMKW configuration, this method short-circuits
   * to a pass-through — no classification, no logging, no `errorType` mutation, and no
   * handler dispatch. This preserves the pre-JMKW-1863 behavior where the entire JMKW
   * tile error interceptor was not registered at all when errors were configured hidden.
   *
   * @param error The error to handle
   * @returns Observable that completes the error handling
   */
  public handleJmkwTileError(
    error: EsriRequestError
  ): Observable<EsriRequestError> {
    if (this._config.hideEsriErrors) {
      return of(error);
    }

    // Classify the error type
    const errorType = this.classifyError(error);
    error.errorType = errorType;

    // Immediately return for Abort errors without processing
    if (errorType === EsriErrorType.ABORT) {
      // Remove detailed logging for abort errors
      return of(error);
    } else {
      this._logger.debug(`[EsriInterceptor] JMKW tile ${errorType} error intercepted:`, error);
    }

    const url = this.extractErrorUrl(error);
    if (!url) {
      this._logger.debug('[EsriInterceptor] No URL found in error object:', error);
      return throwError(() => error);
    }

    return this.processErrorHandlers(error, url).pipe(
      catchError((innerError) => {
        this._logger.error('[EsriInterceptor] Error occurred during error handling:', innerError);
        return throwError(() => error);
      })
    );
  }

  /**
   * Extract URL from an error object
   * @param error The error containing URL information
   * @returns The extracted URL or undefined
   */
  private extractErrorUrl(error: EsriRequestError): string | undefined {
    return error.url || error.details?.url || error.request?.url;
  }

  /**
   * Process registered error handlers for a specific URL
   * @param error The error to process
   * @param url The URL that generated the error
   * @returns Observable that completes the error processing
   */
  private processErrorHandlers(
    error: EsriRequestError,
    url: string
  ): Observable<EsriRequestError> {
    let handled = false;
    let modifiedError = error;

    for (const [urlPattern, handlers] of this._errorHandlers.entries()) {
      if (url.includes(urlPattern)) {
        const applicableHandlers = handlers.filter((registered) =>
          this.shouldHandleError(error, registered.options)
        );

        this._logger.debug(
          `[EsriInterceptor] Found ${applicableHandlers.length} applicable handler(s) for pattern '${urlPattern}' matching URL: ${url}`
        );

        if (applicableHandlers.length > 0) {
          handled = true;
        }

        applicableHandlers.forEach(({ handler, options }) => {
          this.executeErrorHandler(handler, modifiedError, options).subscribe({
            next: (result) => {
              if (result !== undefined) {
                modifiedError = result;
              }
            },
            error: (handlerError) => {
              this._logger.error(
                `[EsriInterceptor] Error handler for pattern '${urlPattern}' threw an exception:`,
                handlerError
              );
            }
          });
        });
      }
    }

    if (handled) {
      return of(modifiedError);
    }

    this._logger.debug(`[EsriInterceptor] No specific error handler found for URL: ${url}`);
    return throwError(() => error);
  }

  /**
   * Determine if an error should be handled based on options
   * @param error The error to check
   * @param options Handler options with include/exclude criteria
   * @returns Whether the error should be handled
   */
  private shouldHandleError(
    error: EsriRequestError,
    options: ErrorHandlerOptions
  ): boolean {
    const errorType = error.errorType || this.classifyError(error);

    // Skip if error type is in excluded types
    if (options.excludeErrorTypes?.includes(errorType)) {
      return false;
    }

    // Only process if included types are specified and the error matches
    if (
      options.includeErrorTypes?.length &&
      !options.includeErrorTypes.includes(errorType)
    ) {
      return false;
    }

    return true;
  }

  /**
   * Execute a specific error handler with appropriate options
   * @param handler The error handler function
   * @param error The error to handle
   * @param options Options for handler execution
   * @returns Observable of the handled error
   */
  private executeErrorHandler(
    handler: EsriErrorHandler,
    error: EsriRequestError,
    options: ErrorHandlerOptions = {}
  ): Observable<EsriRequestError> {
    let observable = handler(error);

    // Apply debounce if specified in options
    if (options.debounceTime !== undefined) {
      observable = observable.pipe(debounceTime(options.debounceTime));
    } else if (options.debounceTime !== 0) {
      // Use default debounce if not explicitly set to 0
      observable = observable.pipe(debounceTime(ERROR_DEBOUNCE_TIME));
    }

    return observable;
  }

  /**
   * Register an error handler for a specific URL pattern
   * @param urlPattern The URL pattern to match for error handling
   * @param options Configuration options for the handler
   * @param handler Optional custom handler function
   * @returns Observable that emits errors matching the pattern
   */
  registerErrorHandler(
    urlPattern: string,
    options: ErrorHandlerOptions = {},
    handler?: EsriErrorHandler
  ): Observable<EsriRequestError> {
    // If interceptor is disabled, return an empty observable
    if (this._config.hideEsriErrors) {
      this._logger.debug(
        '[EsriInterceptor] Error handler registration skipped - interceptor disabled by configuration'
      );
      return new Observable<EsriRequestError>((observer) => {
        // Return a cleanup function that does nothing
        return () => {};
      });
    }

    // Ensure AbortError is excluded by default unless explicitly included
    if (
      !options.includeErrorTypes?.includes(EsriErrorType.ABORT) &&
      !options.excludeErrorTypes?.includes(EsriErrorType.ABORT)
    ) {
      options.excludeErrorTypes = [
        ...(options.excludeErrorTypes || []),
        EsriErrorType.ABORT
      ];
    }

    const subject = new Subject<EsriRequestError>();

    // Create a wrapper handler that will both process the error and emit to the subject
    const wrapperHandler = (
      error: EsriRequestError
    ): Observable<EsriRequestError> => {
      // Only emit errors that should be handled
      if (this.shouldHandleError(error, options)) {
        subject.next(error);
      }

      // If a custom handler was provided, use it; otherwise return the original error
      if (handler) {
        return handler(error);
      }
      return of(error);
    };

    // Register the wrapper handler
    const existingHandlers = this._errorHandlers.get(urlPattern) || [];
    existingHandlers.push({ handler: wrapperHandler, options });
    this._errorHandlers.set(urlPattern, existingHandlers);

    return new Observable<EsriRequestError>((observer) => {
      const subscription = subject.subscribe(observer);

      // Return cleanup function to unsubscribe and remove handler
      return () => {
        subscription.unsubscribe();
        this.removeHandlerFromMap(urlPattern, wrapperHandler);
      };
    }).pipe(
      // Ensure cleanup happens when the observable completes
      finalize(() => {
        this.removeHandlerFromMap(urlPattern, wrapperHandler);
      })
    );
  }

  /**
   * Remove a specific handler from the internal map
   * @param urlPattern The URL pattern to remove from
   * @param handlerToRemove The specific handler to remove
   */
  private removeHandlerFromMap(
    urlPattern: string,
    handlerToRemove?: EsriErrorHandler
  ): void {
    if (!handlerToRemove) return;

    const handlers = this._errorHandlers.get(urlPattern);
    if (handlers) {
      const index = handlers.findIndex(
        ({ handler }) => handler === handlerToRemove
      );
      if (index !== -1) {
        handlers.splice(index, 1);
        if (handlers.length === 0) {
          this._errorHandlers.delete(urlPattern);
          this._logger.debug(`[EsriInterceptor] Removed last handler for pattern: ${urlPattern}`);
        } else {
          this._errorHandlers.set(urlPattern, handlers);
          this._logger.debug(
            `[EsriInterceptor] Removed handler for pattern: ${urlPattern}, ${handlers.length} remaining`
          );
        }
      }
    }
  }

  /**
   * Remove all error handlers for a URL pattern
   * @param urlPattern The URL pattern to remove handlers for
   */
  removeErrorHandler(urlPattern: string): void {
    const hadHandlers = this._errorHandlers.has(urlPattern);
    this._errorHandlers.delete(urlPattern);
    if (hadHandlers) {
      this._logger.debug(
        `[EsriInterceptor] Removed all error handlers for pattern: ${urlPattern}`
      );
    }
  }

  /**
   * Remove a specific error handler for a URL pattern
   * @param urlPattern The URL pattern to remove from
   * @param handler The specific handler to remove
   * @returns Whether the handler was found and removed
   */
  removeSpecificErrorHandler(
    urlPattern: string,
    handler: EsriErrorHandler
  ): boolean {
    const handlers = this._errorHandlers.get(urlPattern);
    if (!handlers) return false;

    const index = handlers.findIndex(
      (registered) => registered.handler === handler
    );
    if (index === -1) return false;

    handlers.splice(index, 1);
    if (handlers.length === 0) {
      this._errorHandlers.delete(urlPattern);
    } else {
      this._errorHandlers.set(urlPattern, handlers);
    }

    this._logger.debug(`[EsriInterceptor] Removed specific handler for pattern: ${urlPattern}`);
    return true;
  }

  /**
   * Get a copy of all registered error handlers
   * @returns Map of URL patterns to handlers
   */
  getErrorHandlers(): Map<
    string,
    Array<{ handler: EsriErrorHandler; options: ErrorHandlerOptions }>
  > {
    return new Map(this._errorHandlers);
  }
}
